import { useState, useEffect, useRef, useCallback } from 'react';

const CHAT_FN = '/api/chat';
const STORAGE_KEY = 'site_chat_session_token_local_v1';
const POLL_INTERVAL = 3000;

interface ChatMessage {
  id: number;
  sender: 'visitor' | 'admin';
  text: string;
  created_at: string;
}

interface OfferRequest {
  title: string;
}

const chatHeaders = {
  'Content-Type': 'application/json',
};

// --- Notification sound (Web Audio API) ---
let audioCtx: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioCtx;
};

const playNotificationSound = () => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;

    // Tone 1 — higher pitch
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.setValueAtTime(880, now);
    osc1.frequency.setValueAtTime(1100, now + 0.06);
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.25, now + 0.02);
    gain1.gain.linearRampToValueAtTime(0, now + 0.25);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.25);

    // Tone 2 — slightly delayed, higher
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.setValueAtTime(1100, now + 0.08);
    osc2.frequency.setValueAtTime(1400, now + 0.14);
    gain2.gain.setValueAtTime(0, now + 0.08);
    gain2.gain.linearRampToValueAtTime(0.2, now + 0.10);
    gain2.gain.linearRampToValueAtTime(0, now + 0.3);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.08);
    osc2.stop(now + 0.3);
  } catch {
    // Silently fail if audio is blocked
  }
};

const detectOfferTitle = (trigger: HTMLElement): string => {
  let node: HTMLElement | null = trigger;

  for (let depth = 0; depth < 7 && node; depth += 1, node = node.parentElement) {
    const text = (node.innerText || node.textContent || '').replace(/\s+/g, ' ');

    if (text.includes('Скидка 10%') || text.includes('-10%')) {
      return 'Скидка 10% на первое сотрудничество';
    }
    if (text.includes('+5 000 ₽') || text.includes('100% предоплат')) {
      return '+5 000 ₽ к рекламному бюджету';
    }
    if (text.includes('Бесплатный месяц поддержки') || text.includes('+1 месяц')) {
      return 'Бесплатный месяц поддержки';
    }
  }

  return 'Спецпредложение Лайт Медиа';
};

// Expose global open function
if (typeof window !== 'undefined') {
  (window as any).__openSiteChat = (prefill?: string) => {
    const event = new CustomEvent('open-site-chat', { detail: { prefill } });
    window.dispatchEvent(event);
  };
}

export default function ChatWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [connected, setConnected] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [offerRequest, setOfferRequest] = useState<OfferRequest | null>(null);
  const [offerName, setOfferName] = useState('');
  const [offerCommunity, setOfferCommunity] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const offerNameRef = useRef<HTMLInputElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastMsgCountRef = useRef(0);
  const prevSoundMsgCountRef = useRef(0);

  // Listen for global open event
  useEffect(() => {
    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ prefill?: string }>;
      const prefill = customEvent.detail?.prefill?.trim();
      if (prefill) {
        setOfferRequest(null);
        setInput(prefill);
      }
      setIsOpen(true);
    };
    window.addEventListener('open-site-chat', handler);
    return () => window.removeEventListener('open-site-chat', handler);
  }, []);

  // Promo buttons "Получить предложение" open the site chat with a short prepared form.
  useEffect(() => {
    const handleOfferClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const trigger = target?.closest('a, button') as HTMLElement | null;
      if (!trigger) return;

      const label = (trigger.innerText || trigger.textContent || '').replace(/\s+/g, ' ').trim();
      if (!label.includes('Получить предложение')) return;

      event.preventDefault();
      const title = detectOfferTitle(trigger);
      setOfferRequest({ title });
      setOfferName('');
      setOfferCommunity('');
      setSendError(null);
      setIsOpen(true);
    };

    document.addEventListener('click', handleOfferClick, true);
    return () => document.removeEventListener('click', handleOfferClick, true);
  }, []);

  // Create or restore session
  const createSession = useCallback(async () => {
    try {
      const res = await fetch(`${CHAT_FN}`, {
        method: 'POST',
        headers: chatHeaders,
        body: JSON.stringify({ action: 'create_session', source_site: 'light_media', source: 'Лайт Медиа' }),
      });
      const data = await res.json();
      if (data.success && data.session?.token) {
        localStorage.setItem(STORAGE_KEY, data.session.token);
        setToken(data.session.token);
        setConnected(true);
        setSendError(null);
        return data.session.token;
      }
    } catch {
      /* ignore */
    }
    return null;
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      setToken(stored);
      setConnected(true);
    }
  }, []);

  useEffect(() => {
    if (isOpen && !token) {
      createSession();
    }
  }, [createSession, isOpen, token]);

  // Poll for messages
  const pollMessages = useCallback(() => {
    if (!token) return;
    fetch(`${CHAT_FN}`, {
      method: 'POST',
      headers: chatHeaders,
      body: JSON.stringify({ action: 'poll', token }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.success && data.error?.includes('session not found')) {
          localStorage.removeItem(STORAGE_KEY);
          setToken(null);
          setConnected(false);
          setMessages([]);
          prevSoundMsgCountRef.current = 0;
          lastMsgCountRef.current = 0;
          void createSession();
          return;
        }
        if (data.success && data.messages) {
          const newMessages: ChatMessage[] = data.messages;
          // Detect new admin messages for sound
          const prevCount = prevSoundMsgCountRef.current;
          const newCount = newMessages.length;

          if (newCount > prevCount && prevCount > 0) {
            const freshMessages = newMessages.slice(prevCount);
            const hasAdminMsg = freshMessages.some((m: ChatMessage) => m.sender === 'admin');
            if (hasAdminMsg) {
              playNotificationSound();
            }
          }

          prevSoundMsgCountRef.current = newCount;
          setMessages(newMessages);
          lastMsgCountRef.current = newCount;
        }
      })
      .catch(() => {});
  }, [token, createSession]);

  // Start/stop polling
  useEffect(() => {
    if (!isOpen || !connected || !token) return;
    pollMessages();
    pollRef.current = setInterval(pollMessages, POLL_INTERVAL);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [connected, isOpen, token, pollMessages]);

  // Scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  // Focus correct field when chat opens
  useEffect(() => {
    if (!isOpen) return;
    setTimeout(() => {
      if (offerRequest) {
        offerNameRef.current?.focus();
      } else {
        inputRef.current?.focus();
      }
    }, 300);
  }, [isOpen, offerRequest]);

  const sendText = async (text: string): Promise<boolean> => {
    let activeToken = token;
    if (!activeToken) {
      activeToken = await createSession();
    }
    if (!activeToken) {
      setSendError('Не удалось подключиться к чату. Попробуйте ещё раз.');
      return false;
    }

    const sendWithToken = async (sessionToken: string) => {
      const params = new URLSearchParams(window.location.search);
      const landingPath = sessionStorage.getItem('light_landing_path') || window.location.pathname;
      if (!sessionStorage.getItem('light_landing_path')) sessionStorage.setItem('light_landing_path', landingPath);
      const tracking = {
        page_path: window.location.pathname + window.location.search,
        referrer: document.referrer || '',
        utm_source: params.get('utm_source') || '',
        utm_medium: params.get('utm_medium') || '',
        utm_campaign: params.get('utm_campaign') || '',
        utm_content: params.get('utm_content') || '',
        landing_path: landingPath,
        device: window.innerWidth <= 768 ? 'mobile' : 'desktop',
      };
      const res = await fetch(`${CHAT_FN}`, {
        method: 'POST',
        headers: chatHeaders,
        body: JSON.stringify({ action: 'send', token: sessionToken, text, source_site: 'light_media', source: 'Лайт Медиа', tracking }),
      });
      return res.json();
    };

    try {
      let data = await sendWithToken(activeToken);

      if (!data.success && data.error?.includes('session not found')) {
        const newToken = await createSession();
        if (!newToken) {
          setSendError('Не удалось создать новую сессию');
          return false;
        }
        data = await sendWithToken(newToken);
      }

      if (!data.success) {
        setSendError('Ошибка отправки: ' + (data.error || 'неизвестно'));
        return false;
      }

      setSendError(null);
      setTimeout(pollMessages, 100);
      return true;
    } catch (err) {
      console.error('Send exception:', err);
      setSendError('Сервер недоступен. Попробуйте позже.');
      return false;
    }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setSendError(null);
    setInput('');

    const ok = await sendText(text);
    if (!ok) {
      setInput(text);
    }

    setSending(false);
    inputRef.current?.focus();
  };

  const handleOfferSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!offerRequest || sending) return;

    const name = offerName.trim();
    let community = offerCommunity.trim();
    if (!name || !community) return;

    if (!/^https?:\/\//i.test(community)) {
      community = `https://${community.replace(/^\/+/, '')}`;
    }

    setSending(true);
    setSendError(null);

    const text = [
      '🎁 Заявка по спецпредложению',
      `Предложение: ${offerRequest.title}`,
      `Имя: ${name}`,
      `Сообщество: ${community}`,
    ].join('\n');

    const ok = await sendText(text);
    if (ok) {
      setOfferRequest(null);
      setOfferName('');
      setOfferCommunity('');
    }

    setSending(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-[100] flex items-center justify-center transition-all duration-300 cursor-pointer ${
          isOpen
            ? 'w-12 h-12 rounded-full bg-gray-800 dark:bg-gray-700 shadow-lg'
            : 'w-12 h-12 rounded-full bg-cyan-500 text-white shadow-lg shadow-cyan-500/30 hover:bg-cyan-400'
        }`}
        aria-label={isOpen ? 'Закрыть чат' : 'Открыть чат'}
      >
        {isOpen ? (
          <i className="ri-close-line text-white text-xl mx-auto"></i>
        ) : (
          <i className="ri-chat-3-line text-white text-xl mx-auto"></i>
        )}
      </button>

      {/* Chat panel */}
      <div
        className={`fixed bottom-24 right-6 z-[100] w-[380px] max-w-[calc(100vw-3rem)] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden transition-all duration-300 origin-bottom-right ${
          isOpen
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 translate-y-4 pointer-events-none'
        }`}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800 bg-gradient-to-r from-cyan-500 to-cyan-600">
          <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
            <i className="ri-chat-smile-2-line text-white text-lg"></i>
          </div>
          <div className="flex-1">
            <div className="text-white font-bold text-sm">Поддержка Лайт Медиа</div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-300 flex-shrink-0"></span>
              <span className="text-cyan-100 text-xs">Отвечаем в течение часа</span>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer flex-shrink-0"
          >
            <i className="ri-close-line text-white text-sm"></i>
          </button>
        </div>

        {/* Messages / offer form */}
        <div className="h-[360px] overflow-y-auto px-4 py-4 space-y-3 bg-gray-50 dark:bg-gray-950">
          {!connected && (
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3">
                <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-xs text-gray-400">Подключаемся...</p>
              </div>
            </div>
          )}

          {connected && offerRequest && (
            <div className="h-full flex items-center justify-center">
              <form onSubmit={handleOfferSubmit} className="w-full bg-white dark:bg-gray-900 border border-cyan-100 dark:border-cyan-900/50 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-9 h-9 rounded-xl bg-cyan-50 dark:bg-cyan-900/30 flex items-center justify-center flex-shrink-0">
                    <i className="ri-gift-line text-cyan-500"></i>
                  </div>
                  <div>
                    <div className="text-[11px] font-bold uppercase tracking-wide text-cyan-500">Спецпредложение</div>
                    <div className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{offerRequest.title}</div>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Предложение уже выбрано. Укажите только имя и ссылку на ваше сообщество.
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Ваше имя</label>
                    <input
                      ref={offerNameRef}
                      type="text"
                      value={offerName}
                      onChange={(e) => setOfferName(e.target.value)}
                      required
                      placeholder="Иван"
                      className="w-full px-3.5 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-semibold text-gray-600 dark:text-gray-300 mb-1">Ссылка на сообщество</label>
                    <input
                      type="text"
                      value={offerCommunity}
                      onChange={(e) => setOfferCommunity(e.target.value)}
                      required
                      placeholder="https://vk.com/your_group"
                      className="w-full px-3.5 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {sendError && (
                  <div className="mt-3 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-lg">
                    {sendError}
                  </div>
                )}

                <div className="flex gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => setOfferRequest(null)}
                    className="px-4 py-2.5 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 text-xs font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  >
                    Отмена
                  </button>
                  <button
                    type="submit"
                    disabled={sending || !connected || !offerName.trim() || !offerCommunity.trim()}
                    className="flex-1 py-2.5 rounded-xl bg-cyan-500 text-white text-xs font-bold hover:bg-cyan-400 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center gap-2"
                  >
                    {sending ? (
                      <><span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span> Отправка...</>
                    ) : (
                      <><i className="ri-send-plane-fill"></i> Отправить</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}

          {connected && !offerRequest && messages.length === 0 && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center px-4">
                <div className="w-14 h-14 mx-auto mb-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-full flex items-center justify-center">
                  <i className="ri-chat-3-line text-cyan-500 text-2xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Здравствуйте!</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">Задайте ваш вопрос — мы ответим в ближайшее время</p>
              </div>
            </div>
          )}

          {connected && !offerRequest && messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.sender === 'visitor' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  msg.sender === 'visitor'
                    ? 'bg-cyan-500 text-white rounded-br-md'
                    : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-100 dark:border-gray-700 rounded-bl-md'
                }`}
              >
                <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                <p
                  className={`text-[10px] mt-1 ${
                    msg.sender === 'visitor' ? 'text-cyan-100' : 'text-gray-400 dark:text-gray-500'
                  }`}
                >
                  {formatTime(msg.created_at)}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        {!offerRequest && (
          <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
            {sendError && (
              <div className="mb-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-xs rounded-lg">
                {sendError}
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Напишите сообщение..."
                disabled={!connected || sending}
                className="flex-1 px-4 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-all disabled:opacity-50"
              />
              <button
                onClick={handleSend}
                disabled={!input.trim() || !connected || sending}
                className="w-10 h-10 flex-shrink-0 bg-cyan-500 text-white rounded-xl hover:bg-cyan-400 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed transition-colors cursor-pointer flex items-center justify-center"
              >
                {sending ? (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <i className="ri-send-plane-fill text-sm"></i>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
